import { axiosInstance } from "./axios";

/** Start deletion, validate its execution, then wait for a confirmed purge. */
export const startAndPollAccountDeletion = async (
  path: string,
  signal?: AbortSignal,
): Promise<boolean> => {
  const { data } = await axiosInstance.delete(path, {
    timeout: 60_000,
    ...(signal ? { signal } : {}),
  });
  // A verified-owner retry may arrive after the previous job purged SQL.
  if (data?.status === "completed") return true;
  if (typeof data?.execution !== "string" || !data.execution.trim()) {
    const detail = "Unable to start account deletion: the server did not return an execution ID. Please try again.";
    throw Object.assign(new Error(detail), {
      isAxiosError: true,
      response: { data: { detail } },
    });
  }
  return pollAccountDeletion(async (pollSignal) => {
    const { data: result } = await axiosInstance.get(`${path}/deletion`, {
      params: { execution: data.execution },
      timeout: 15_000,
      signal: pollSignal,
    });
    return result;
  }, signal);
};

type DeletionStatus = { status: string; error?: string; errorStatus?: number; errorCode?: string };

/** Stop waiting locally after three minutes; the server job keeps running. */
const pollAccountDeletion = async (
  check: (signal: AbortSignal) => Promise<DeletionStatus>,
  signal?: AbortSignal,
): Promise<boolean> => {
  const controller = new AbortController();
  let rejectStopped: (error: Error) => void = () => {};
  let stopError: Error | undefined;
  const stopped = new Promise<never>((_, reject) => { rejectStopped = reject; });
  const stop = (error: Error) => {
    if (stopError) return;
    stopError = error;
    rejectStopped(error);
    controller.abort();
  };
  const abort = () => stop(Object.assign(new Error("Account deletion polling cancelled"), { name: "AbortError" }));
  const detail = "Account deletion is still processing. Please check back in a few minutes. Your login has been kept until deletion is confirmed.";
  const deadline = setTimeout(() => stop(Object.assign(new Error(detail), {
    code: "DELETION_PENDING", isAxiosError: true,
    response: { data: { detail } },
  })), 180_000);
  signal?.addEventListener("abort", abort);
  if (signal?.aborted) abort();
  const assertActive = () => { if (stopError) throw stopError; };
  const delay = () => new Promise<void>((resolve, reject) => {
    const cancel = () => { clearTimeout(timer); reject(stopError); };
    const timer = setTimeout(() => {
      controller.signal.removeEventListener("abort", cancel);
      resolve();
    }, 3000);
    controller.signal.addEventListener("abort", cancel, { once: true });
  });
  const poll = async () => {
    for (;;) {
      assertActive();
      const result = await check(controller.signal);
      assertActive();
      if (result?.status === "completed") return true;
      if (result?.status === "failed") {
        const message = result.error || "Account deletion failed";
        throw Object.assign(new Error(message), {
          isAxiosError: true,
          response: {
            status: result.errorStatus,
            data: { detail: message, ...(result.errorCode ? { code: result.errorCode } : {}) },
          },
        });
      }
      if (result?.status !== "running") throw new Error("Invalid account deletion status");
      await delay();
    }
  };
  try {
    return await Promise.race([poll(), stopped]);
  } finally {
    clearTimeout(deadline);
    signal?.removeEventListener("abort", abort);
  }
};
